import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import "../style.css";
import PageLoader from "../components/PageLoader";
import SEO from "../components/SEO";
import SiteFooter from "../components/SiteFooter";

const whatsappNumber = "918838619488";

const normalizeImageValues = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) return value;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        return Array.isArray(parsed) ? parsed : [trimmed];
      } catch {
        return [trimmed];
      }
    }

    if (trimmed.includes(",")) {
      return trimmed
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return [trimmed];
  }

  if (typeof value === "object") {
    return Object.values(value);
  }

  return [];
};

const getProductImages = (laptop) => {
  const base = [
    ...normalizeImageValues(laptop?.images),
    ...normalizeImageValues(laptop?.gallery),
    laptop?.image,
    laptop?.image1,
    laptop?.image2,
    laptop?.image3,
  ];

  return [...new Set(base.map((img) => String(img || "").trim()).filter(Boolean))];
};

function Home() {
  const [laptops, setLaptops] = useState([]);
  const [sliders, setSliders] = useState([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const loadHomeData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");
      const [laptopSnapshot, sliderSnapshot] = await Promise.all([
        getDocs(collection(db, "laptops")),
        getDocs(collection(db, "sliders")),
      ]);

      const laptopsData = laptopSnapshot.docs.map((item) => ({
        _id: item.id,
        ...item.data(),
      }));
      laptopsData.sort((a, b) => b._id.localeCompare(a._id));
      setLaptops(laptopsData);

      const slidersData = sliderSnapshot.docs
        .map((item) => ({ _id: item.id, ...item.data() }))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      setSliders(slidersData);
    } catch (error) {
      console.error("Error loading home data:", error);
      setLoadError("Unable to load latest products right now. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHomeData();
  }, [loadHomeData]);

  useEffect(() => {
    if (sliders.length <= 1) return undefined;

    const intervalId = setInterval(() => {
      setActiveSlide((previous) => (previous + 1) % sliders.length);
    }, 3500);

    return () => clearInterval(intervalId);
  }, [sliders]);

  useEffect(() => {
    if (activeSlide >= sliders.length) setActiveSlide(0);
  }, [activeSlide, sliders.length]);

  const nextSlide = () => {
    if (sliders.length <= 1) return;
    setActiveSlide((previous) => (previous + 1) % sliders.length);
  };

  const previousSlide = () => {
    if (sliders.length <= 1) return;
    setActiveSlide(
      (previous) => (previous - 1 + sliders.length) % sliders.length
    );
  };

  const getWhatsAppLink = (laptop) => {
    const displayPrice = laptop.discountPrice || laptop.price;
    const message = `Hello! I am interested in this laptop:

Name: ${laptop.name}
Price: Rs ${displayPrice}
Specs: ${laptop.specs}`;

    return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
  };

  const getDiscountPercent = (laptop) => {
    const price = Number(laptop.price);
    const discount = Number(laptop.discountPrice);
    if (!discount || !price || discount >= price) return null;
    return Math.round((1 - discount / price) * 100);
  };

  const featuredLaptops = useMemo(() => laptops.slice(0, 12), [laptops]);

  return (
    <>
      <SEO
        title="Lappy Used Laptops | Best deals in Namakkal"
        description="Best place to buy used & refurbished laptops in Namakkal. Affordable prices, warranty, service and accessories available."
        url="https://lappylaptops.com/"
      />

      <PageLoader>
        <div className="home-ecom">
          {loadError && (
            <div className="page-error-banner">
              <p>{loadError}</p>
              <button type="button" className="btn-primary" onClick={loadHomeData}>
                Retry
              </button>
            </div>
          )}

          <section className="home-ecom-hero">
            <div className="home-ecom-hero-copy">
              <span className="home-ecom-kicker">Lappy Laptops</span>
              <h1>
                <span>Your One Stop Shop</span>
                <span>for Laptops!</span>
              </h1>
              <p className="home-ecom-hero-subtitle">
                Used and refurbished laptops, warranty service, and accessories
                in Namakkal.
              </p>

              <div className="home-ecom-hero-actions">
                <Link to="/products" className="home-ecom-btn home-ecom-btn-main">
                  Buy Now
                </Link>
                <a
                  href={`https://wa.me/${whatsappNumber}`}
                  target="_blank"
                  rel="noreferrer"
                  className="home-ecom-btn home-ecom-btn-alt"
                >
                  Contact Us
                </a>
              </div>
            </div>

            <div className="home-ecom-hero-media">
              {sliders.length > 0 ? (
                <div className="home-ecom-slider">
                  {sliders.map((slide, index) => (
                    <img
                      key={slide._id}
                      src={slide.image}
                      alt={slide.title || `Slide ${index + 1}`}
                      className={`home-ecom-slide ${
                        index === activeSlide ? "active" : ""
                      }`}
                      loading={index === 0 ? "eager" : "lazy"}
                    />
                  ))}

                  {sliders.length > 1 && (
                    <>
                      <div className="home-ecom-slider-nav">
                        <button type="button" onClick={previousSlide}>
                          Prev
                        </button>
                        <button type="button" onClick={nextSlide}>
                          Next
                        </button>
                      </div>

                      <div className="home-ecom-slider-dots">
                        {sliders.map((slide, index) => (
                          <button
                            key={slide._id}
                            type="button"
                            className={index === activeSlide ? "active" : ""}
                            onClick={() => setActiveSlide(index)}
                            aria-label={`Go to slide ${index + 1}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="home-ecom-slider home-ecom-slider-empty">
                  <p>Add slider images from admin panel.</p>
                </div>
              )}

            </div>
          </section>

          <section className="home-ecom-featured">
            <div className="home-ecom-section-head">
              <h2>Featured Laptops</h2>
            </div>

            <div className="home-ecom-product-grid">
              {loading
                ? Array.from({ length: 12 }).map((_, i) => (
                    <div className="home-ecom-product-card skeleton" key={i}>
                      <div className="home-ecom-product-image skeleton-block"></div>
                      <div className="skeleton-line long"></div>
                      <div className="skeleton-line medium"></div>
                      <div className="skeleton-line short"></div>
                    </div>
                  ))
                : featuredLaptops.map((laptop) => {
                    const productImages = getProductImages(laptop);
                    const mainImage = productImages[0] || "placeholder.png";
                    const discountPercent = getDiscountPercent(laptop);
                    return (
                      <div key={laptop._id} className="home-ecom-product-card">
                        <Link
                          to={`/products/${laptop._id}`}
                          className="product-image-link"
                        >
                          <div className="home-ecom-product-image-wrap">
                            {discountPercent && (
                              <span className="discount-badge-top">
                                {discountPercent}% OFF
                              </span>
                            )}
                            <img
                              className="home-ecom-product-image"
                              src={mainImage}
                              alt={laptop.name}
                              onError={(event) =>
                                (event.currentTarget.src = "placeholder.png")
                              }
                            />
                          </div>
                        </Link>
                        <Link
                          to={`/products/${laptop._id}`}
                          className="product-link"
                        >
                          <h3>{laptop.name}</h3>
                        </Link>
                        <div className="price-row">
                          {laptop.discountPrice ? (
                            <>
                              <span className="price-new">
                                ₹{laptop.discountPrice}
                              </span>
                              <span className="price-old">
                                ₹{laptop.price}
                              </span>
                            </>
                          ) : (
                            <span className="price-new">₹{laptop.price}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            window.open(getWhatsAppLink(laptop), "_blank")
                          }
                        >
                          Contact Now
                        </button>
                      </div>
                    );
                  })}
            </div>

            {laptops.length > 12 && (
              <div className="home-ecom-center">
                <Link to="/products" className="home-ecom-btn home-ecom-btn-main">
                  Browse All Products
                </Link>
              </div>
            )}
          </section>

          <section className="home-ecom-services-preview">
            <div className="home-ecom-services-head">
              <h2>Our Services</h2>
            </div>

            <div className="home-ecom-services-grid">
              <article className="home-ecom-service-card">
                <span className="home-ecom-service-icon">&#128187;</span>
                <h3>Laptop Sales</h3>
                <p>Latest laptops with best price and warranty.</p>
              </article>

              <article className="home-ecom-service-card">
                <span className="home-ecom-service-icon">&#128736;</span>
                <h3>Laptop Repair</h3>
                <p>Fast and trusted repair by expert technicians.</p>
              </article>

              <article className="home-ecom-service-card">
                <span className="home-ecom-service-icon">&#127911;</span>
                <h3>Accessories</h3>
                <p>Headphones, chargers and premium add-ons.</p>
              </article>
            </div>
          </section>

        </div>
      </PageLoader>

      <section className="contact">
        <div>
          <h2 className="get">Get in Touch</h2>
          <p>📞 +91 8838619488</p>
          <p>✉️ <a href="mailto:lappylaptopsales@gmail.com" className="contact-email-link">lappylaptopsales@gmail.com</a></p>
          <p>📍 129 Salapalayam Road, Reddipatti, Namakkal, TamilNadu 637002</p>
          <p>📍 Lappy Laptops, 27, Devangar Street, Thathiengarpettai, Musiri-Tk, Trichy-Dt, Pincode: 621214. Landmark: Mini Radios.</p>
          <a
            href={`https://wa.me/${whatsappNumber}`}
            target="_blank"
            rel="noreferrer"
            className="whatsapp"
          >
            WhatsApp Us
          </a>
        </div>

        <div className="map-container">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3913.730200578514!2d78.22037987429323!3d11.207586751094425!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3babc94be384bbb3%3A0xcdd0430efbea64f3!2sLappy%20Used%20Laptops%20Sales!5e0!3m2!1sen!2sin!4v1770279618891!5m2!1sen!2sin"
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            title="Lappy Location"
          ></iframe>
        </div>
      </section>

      <SiteFooter />
    </>
  );
}

export default Home;


